import { forwardRef, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { LayoutGrid } from 'lucide-react'
import TableShape from '@/components/seating_planner/TableShape'
import { cn } from '@/lib/utils'
import type { PlanState, SeatAssignment, SeatingGuest, SeatingTable } from '@/types/seatingPlanner'

type CanvasProps = {
  state: PlanState
  activeDropTableId: string | null
  activeDropSeat: { tableId: string; seatIndex: number } | null
  onUpdateTable: (id: string, patch: Partial<SeatingTable>) => void
  onDeleteTable: (id: string) => void
  onUnassignGuest: (guestId: string) => void
  onScaleChange?: (scale: number) => void
}

type TableSeating = {
  seats: Array<SeatingGuest | undefined>
  count: number
}

export const CANVAS_WIDTH = 1600
export const CANVAS_HEIGHT = 1000

const Canvas = forwardRef<HTMLDivElement, CanvasProps>(function Canvas(
  { state, activeDropTableId, activeDropSeat, onUpdateTable, onDeleteTable, onUnassignGuest, onScaleChange },
  ref,
) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [scale, setScale] = useState(1)

  useLayoutEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const update = () => {
      const next = Math.min(1, el.clientWidth / CANVAS_WIDTH)
      setScale(next)
      onScaleChange?.(next)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [onScaleChange])

  const seatingByTable = useMemo(
    () => groupGuestsByTable(state.guests, state.assignments, state.tables),
    [state.guests, state.assignments, state.tables],
  )

  return (
    <div ref={wrapperRef} className="relative w-full" style={{ height: CANVAS_HEIGHT * scale }}>
      <div
        style={{
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <div
          ref={ref}
          data-canvas
          className="relative overflow-hidden rounded-xl bg-white ring-1 ring-black/10 shadow-inner"
          style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
        >
          <GridBackdrop />

          {state.tables.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground pointer-events-none">
              <LayoutGrid className="size-10 opacity-30" />
              <p className="text-sm font-medium">Add tables from the toolbar to get started.</p>
            </div>
          )}

          {state.tables.map((table) => {
            const seating = seatingByTable[table.id] || { seats: [], count: 0 }
            return (
              <TableShape
                key={table.id}
                table={table}
                seatedGuests={seating.seats}
                assignedCount={seating.count}
                canvasScale={scale}
                detailed={state.view.detailedTables}
                isDropTarget={activeDropTableId === table.id}
                activeDropSeatIndex={
                  activeDropSeat && activeDropSeat.tableId === table.id ? activeDropSeat.seatIndex : null
                }
                onUpdate={(patch) => onUpdateTable(table.id, patch)}
                onDelete={() => onDeleteTable(table.id)}
                onUnassignGuest={onUnassignGuest}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
})

function GridBackdrop() {
  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-0 opacity-30',
        '[background-image:linear-gradient(to_right,rgb(0,0,0,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgb(0,0,0,0.06)_1px,transparent_1px)]',
        '[background-size:40px_40px]',
      )}
    />
  )
}

function groupGuestsByTable(
  guests: SeatingGuest[],
  assignments: Record<string, SeatAssignment>,
  tables: SeatingTable[],
): Record<string, TableSeating> {
  const out: Record<string, TableSeating> = {}
  for (const table of tables) {
    out[table.id] = { seats: new Array(table.capacity), count: 0 }
  }
  const guestsById = new Map(guests.map((g) => [g.id, g]))
  for (const [guestId, a] of Object.entries(assignments)) {
    const guest = guestsById.get(guestId)
    const slot = out[a.tableId]
    if (!guest || !slot) continue
    slot.count += 1
    if (a.seatIndex >= 0 && a.seatIndex < slot.seats.length) {
      slot.seats[a.seatIndex] = guest
    }
  }
  return out
}

export default Canvas
